import * as Common from '@piccolohealth/pbs-common';
import { P } from '@piccolohealth/util';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import React from 'react';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { Editor } from '../../components/tiptap/Editor';
import { useEditor } from '../../components/tiptap/hooks/useEditor';
import { useHocuspocusProvider } from '../../components/tiptap/hooks/useHocuspocusProvider';
import { useImageUpload } from '../../components/tiptap/hooks/useImageUpload';
import { EditorLayout } from '../../components/tiptap/layout/EditorLayout';
import { EditorMenu } from '../../components/tiptap/menus/EditorMenu';
import { getParticipantNoteExtensions } from '../../components/tiptap/utils';
import { useParticipantNoteQuery } from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantNoteTitleBar } from './ParticipantNoteTitleBar';

interface ParticipantNoteEditorContentProps {
  participantNote: Common.ParticipantNote;
  isEditable: boolean;
  onDelete: () => void;
}

export const ParticipantNoteEditorContent = (props: ParticipantNoteEditorContentProps) => {
  const { participantNote, isEditable, onDelete } = props;

  const { config, organization } = useAppContext();

  const documentId = `participantNote/${participantNote.id}`;

  const { provider, user, state } = useHocuspocusProvider({
    documentId,
  });

  const onImageUpload = useImageUpload({
    imageUploadUrl: `${config.api.webUrl}/organizations/${organization.id}/participants/${participantNote.participantId}/participantNotes/${participantNote.id}/images`,
  });

  const extensions = getParticipantNoteExtensions({ onImageUpload });

  const { editor } = useEditor(
    {
      isEditable,
      extensions: P.compact([
        ...extensions,
        Collaboration.configure({
          document: provider.document,
        }),
        // Read only editors we don't want to have any "awareness" in the backend.
        // I.e. they are viewing this in read only mode.
        isEditable
          ? CollaborationCursor.configure({
              provider,
              user,
            })
          : null,
      ]),
    },
    [isEditable],
  );

  if (!editor) {
    return null;
  }

  return (
    <EditorLayout
      documentId={documentId}
      state={state}
      isSidebarOpen={false}
      headerContent={
        <ParticipantNoteTitleBar
          participantNote={participantNote}
          editor={editor}
          state={state}
          isDisabled={!isEditable}
          onDelete={onDelete}
        />
      }
      toolbarContent={<EditorMenu editor={editor} />}
      mainContent={<Editor editor={editor} />}
      sidebarContent={null}
    />
  );
};

interface Props {
  participantId: string;
  participantNoteId: string;
  isDisabled?: boolean;
  onDelete: () => void;
}

export const ParticipantNoteEditor = (props: Props) => {
  const { participantId, participantNoteId, isDisabled, onDelete } = props;

  const { organization } = useAppContext();

  const { isLoading, error, data } = useParticipantNoteQuery({
    organizationId: organization.id,
    participantId,
    participantNoteId,
  });

  const participantNote = data?.organization?.participant?.note as Common.ParticipantNote | null;

  if (isLoading) {
    return null;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  if (!participantNote) {
    return (
      <PiccoloError
        error={
          new Common.PiccoloError({
            type: 'ParticipantNoteNotFound',
            message: 'Participant note not found',
          })
        }
      />
    );
  }

  return (
    <ParticipantNoteEditorContent
      participantNote={participantNote}
      isEditable={!isDisabled}
      onDelete={onDelete}
    />
  );
};
